import { c as _c } from "react/compiler-runtime";
import { XG_SEAT_REQUIRED_TITLE, XgSeatRequiredForRoleDialog } from "@components/table/members/assign-seats/xg-seat-required-for-role-dialog";
import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { useCompanyId } from "@context-providers/company-id-provider";
import { Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { areCompanyMembersLoadingSelector, companyMembersByKeySelector, fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { fetchCompanyMembers } from "@store/members/members-slice-thunk";
import { isScanPlanSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { formatUserRoleType } from "@utils/data-display";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { getPrettyName } from "@utils/user-utils";
import { isMissingXgSeat } from "@utils/invite-xg-seat-utils";
import { getTeamXgSeatRoleMessage } from "@utils/xg-seat-utils";
import { useEffect, useState } from "react";

/** The events tracked while checking the Sphere XG seat a role needs, e.g. ProjectMemberEvents */

/** Which roles of a subject, e.g. a project or a group, need a Sphere XG seat on a Scan plan */

/**
 * Applies the Sphere XG seats to changing the role of a member in a project or a group, like
 * inviting one does (see useInviteXgSeats). On a Scan plan, only members holding a seat can take on
 * the roles of the subject that need one. Teams cannot hold a seat, so they can only be viewers.
 */
export function useRoleXgSeat({
  subject: {
    xgSeatRoles,
    viewerRole,
    events
  },
  member,
  currentRole = member?.role,
  applyRoleChange,
  onXgSeatDialogClose
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    trackEvent
  } = useTrackEvent();
  const {
    createDialog
  } = useSphereDialog();
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  // Read from the company context, since only workspace admins may read the subscriptions
  const isScanPlan = useAppSelector(isScanPlanSelector);
  const workspaceMember = useWorkspaceMember(member);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    hasFetchingCompanyMembersFailed
  } = useAppSelector(fetchingMembersFlagsSelector);
  const [roleAwaitingXgSeat, setRoleAwaitingXgSeat] = useState();
  const hasMember = member !== undefined;

  // Project and group members do not list their seats, which only the workspace members do
  useEffect(() => {
    if (hasMember && isScanPlan) {
      void dispatch(fetchCompanyMembers({
        companyId
      }));
    }
  }, [companyId, dispatch, hasMember, isScanPlan]);

  // Until the seats are known, every member would look like they lack one
  const isCheckingXgSeats = hasMember && isScanPlan && !FaroCoreApiUtils.Member.isTeam(member) && areMembersLoading;
  const disabledRoles = isCheckingXgSeats ? xgSeatRoles.filter(role => role !== currentRole) : [];
  async function requestRoleChange(role_0) {
    if (disabledRoles.includes(role_0)) {
      return false;
    }
    if (!isXgSeatMissingFor(role_0)) {
      await applyRoleChange(role_0);
      return false;
    }
    if (!member) {
      return false;
    }
    if (!workspaceMember && hasFetchingCompanyMembersFailed) {
      await showXgSeatRequiredDialog(<Text variant="bodyM">
          The Sphere XG seats of the workspace could not be loaded, so the role
          cannot be changed. Please reload the page and try again.
        </Text>);
      return false;
    }
    if (canManageXgSeats && !FaroCoreApiUtils.Member.isTeam(member)) {
      trackEvent({
        name: events.openXgSeatRequiredForRole,
        props: {
          role: role_0
        }
      });
      setRoleAwaitingXgSeat(role_0);
      return true;
    }
    trackEvent({
      name: events.blockXgSeatRequiredForRole,
      props: {
        role: role_0,
        isTeam: FaroCoreApiUtils.Member.isTeam(member)
      }
    });
    await showXgSeatRequiredDialog(<XgSeatNotAssignableMessage member={member} role={role_0} viewerRole={viewerRole} />);
    return false;
  }

  /** Tells the user why the role cannot be changed, in a dialog that only closes */
  async function showXgSeatRequiredDialog(content) {
    await createDialog({
      title: XG_SEAT_REQUIRED_TITLE,
      confirmText: "Close",
      isHideCancel: true
    }, content);
  }

  /** Whether the member lacks the Sphere XG seat that the role needs */
  function isXgSeatMissingFor(role_1) {
    if (!member || !isScanPlan || role_1 === currentRole || !xgSeatRoles.includes(role_1)) {
      return false;
    }
    return FaroCoreApiUtils.Member.isTeam(member) || !workspaceMember || isMissingXgSeat(workspaceMember);
  }

  /** Closes the dialog to assign the seat */
  function closeXgSeatDialog() {
    setRoleAwaitingXgSeat(undefined);
    onXgSeatDialogClose?.();
  }
  const memberToSeat = workspaceMember ?? member;
  const xgSeatDialog = roleAwaitingXgSeat && memberToSeat ? <XgSeatRequiredForRoleDialog member={memberToSeat} role={roleAwaitingXgSeat} events={events} onClose={closeXgSeatDialog} onSeatAssigned={() => applyRoleChange(roleAwaitingXgSeat)} /> : null;
  return {
    requestRoleChange,
    xgSeatDialog,
    disabledRoles
  };
}

/**
 * Returns the workspace member of a project or group member, who holds the seats, by identity or
 * else by email. Undefined while the workspace members are not known.
 */
function useWorkspaceMember(member) {
  const $ = _c(4);
  const membersByKey = useAppSelector(companyMembersByKeySelector);
  if (!member) {
    return;
  }
  let t0;
  if ($[0] !== member.email || $[1] !== member.identity || $[2] !== membersByKey) {
    t0 = membersByKey.get(member.identity) ?? membersByKey.get(member.email.toLowerCase());
    $[0] = member.email;
    $[1] = member.identity;
    $[2] = membersByKey;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
/** Tells why the member cannot get the role, when the user cannot assign them the seat */
function XgSeatNotAssignableMessage(t0) {
  const $ = _c(16);
  const {
    member,
    role,
    viewerRole
  } = t0;
  let t1;
  if ($[0] !== role) {
    t1 = formatUserRoleType(role);
    $[0] = role;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <strong>{t1}</strong>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const roleName = t2;
  if (FaroCoreApiUtils.Member.isTeam(member)) {
    let t3;
    if ($[4] !== viewerRole) {
      t3 = getTeamXgSeatRoleMessage(viewerRole);
      $[4] = viewerRole;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== roleName || $[7] !== t3) {
      t4 = <Text variant="bodyM">Only members with a Sphere XG seat can be {roleName} in this workspace.{" "}{t3}</Text>;
      $[6] = roleName;
      $[7] = t3;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t3;
  if ($[9] !== member) {
    t3 = getPrettyName(member);
    $[9] = member;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t3) {
    t4 = <strong><var>{t3}</var></strong>;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== roleName || $[14] !== t4) {
    t5 = <Text variant="bodyM">{t4}{" "}needs a Sphere XG seat to be {roleName}, and you do not have permission to assign seats. Please contact an admin of the workspace.</Text>;
    $[13] = roleName;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}